import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import isEqual from "react-fast-compare";
import { useMediaQuery } from "react-responsive";
import { AnimatePresence } from "motion/react";
import { useGesture } from "@use-gesture/react";
import classNames from "classnames";
import useWindowScroll from "../../hooks/useWindowScroll";
import SectionMarker from "./SectionMarker";
import ContentSlide from "./ContentSlide";
import { cockpitComponent } from "../CockpitLayoutComponents";
import NextSlideButton from "./NextSlideButton";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import "./style.scss";

const ContentTrack = forwardRef(function ContentTrackRender({ sections }, ref) {
  const contentSlides = useRef([]);

  const getContentSlide = index => contentSlides.current[index];

  useImperativeHandle(ref, () => ({
    getContentSlide
  }));
  return (
    <div className="content-track">
      {sections.map((section, i) => (
        <ContentSlide
          key={i}
          index={i}
          ref={elements => {
            contentSlides.current.push(elements);
          }}
        >
          {section.map(cockpitComponent)}
        </ContentSlide>
      ))}
    </div>
  );
});

const DeepMemoContentTrack = React.memo(ContentTrack, isEqual);

const SectionNavigation = ({
  children,
  direction = "vertical",
  showMenu = false,
  className
}) => {
  const isTouchDevice = useMediaQuery({ query: "(pointer: coarse)" });
  const isLargeScreen = useMediaQuery({ minWidth: "1200px" });
  const container = useRef(document.documentElement);
  const scrollPos = useWindowScroll(container, direction, 100);
  const [activeIndex, setActiveIndex] = useState(0);
  const _activeIndex = useRef(activeIndex);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [hovered, setHovered] = useState(false);
  const [introTimer, setIntroTimer] = useState(true);
  const [disableBodyScroll, setDisableBodyScroll] = useState(false);
  const t = useBjelinTranslations();
  const contentTrack = useRef();
  const getSlide = useCallback(
    i => contentTrack.current.getContentSlide(i),
    [contentTrack]
  );
  const sectionNavigationContentRef = useRef();

  const sections = children.reduce(
    (res, component) => {
      if (component.settings.nav_title) {
        res.push([{ ...component }]);
      } else {
        res[res.length - 1].push({ ...component });
      }
      return res;
    },
    [[]]
  );
  const sectionCount = sections.length;

  const scrollAttr = direction === "vertical" ? "top" : "left";
  const offsetAttr = direction === "vertical" ? "offsetTop" : "offsetLeft";
  const SCROLL_PADDING = direction === "vertical" ? 64 : 0;

  useEffect(() => {
    document.body.style.overflow = disableBodyScroll ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [disableBodyScroll]);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      setIntroTimer(false);
    }, 2000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useLayoutEffect(() => {
    const slidePositions = Array(sectionCount)
      .fill(0)
      .map((_, i) => getSlide(i)[offsetAttr]);
    let nearestIndex =
      scrollPos === 0 && slidePositions[slidePositions.length - 1] === 0
        ? 0
        : slidePositions.findIndex(pos => pos - 100 > scrollPos);
    nearestIndex = nearestIndex === -1 ? sectionCount - 1 : nearestIndex - 1;
    if (_activeIndex.current !== nearestIndex) {
      setActiveIndex(Math.max(0, nearestIndex));
      _activeIndex.current = nearestIndex; //to avoid having activeIndex as dependency
    }
  }, [scrollPos, getSlide, offsetAttr, sectionCount]);

  function onDrag({ target, xy: [, y] }) {
    if (target.id !== "section-navigation-touch-area") return;
    const el = sectionNavigationContentRef.current;
    const elmTop = el.getBoundingClientRect().y;
    const elmBottom = elmTop + el.offsetHeight;
    const index = Math.max(
      0,
      Math.min(
        sectionCount - 1,
        Math.ceil(((y - elmTop) / (elmBottom - elmTop)) * sectionCount) - 1
      )
    );
    if (index !== hoveredIndex) setHoveredIndex(index);
  }

  function onDragStart({ target }) {
    if (target.id !== "section-navigation-touch-area") return;
    setHovered(true);
    setDisableBodyScroll(true);
  }

  function onDragEnd({ target }) {
    if (target.id !== "section-navigation-touch-area") return;
    const nextIndex = hoveredIndex;
    setDisableBodyScroll(false);
    if (
      nextIndex !== activeIndex &&
      nextIndex >= 0 &&
      nextIndex < sectionCount
    ) {
      const slide = contentTrack.current.getContentSlide(nextIndex);

      container.scrollTo({
        [scrollAttr]: slide[offsetAttr] - SCROLL_PADDING,
        behavior: "smooth"
      });
    }
    setTimeout(() => {
      setHovered(false);
      setHoveredIndex(-1);
    }, 200);
  }

  const bindGestures = useGesture({
    onDrag: state => onDrag(state),
    onDragStart: state => onDragStart(state),
    onDragEnd: state => onDragEnd(state)
  });

  return (
    <>
      <div
        ref={direction === "horizontal" ? container : undefined}
        className={classNames("page-sections", "page-sections-" + direction)}
      >
        <DeepMemoContentTrack
          sections={sections}
          direction={direction}
          ref={contentTrack}
        />
      </div>
      {!showMenu && direction === "horizontal" /* back */ && (
        <NextSlideButton
          onClick={() => {
            const nextIndex = Math.max(activeIndex - 1, 0);

            setActiveIndex(nextIndex);
            container.scrollTo({
              [scrollAttr]: getSlide(nextIndex)[offsetAttr] - SCROLL_PADDING,
              behavior: "smooth"
            });
          }}
          slideDirection={direction}
          direction={-1}
          displayInside
          disabled={activeIndex <= 0}
          ariaHidden
        />
      )}
      {!showMenu &&
        (direction === "horizontal" || !isTouchDevice) /* forward */ && (
          <NextSlideButton
            onClick={() => {
              const nextIndex = Math.min(activeIndex + 1, sectionCount - 1);

              setActiveIndex(nextIndex);
              container.scrollTo({
                [scrollAttr]: getSlide(nextIndex)[offsetAttr] - SCROLL_PADDING,
                behavior: "smooth"
              });
            }}
            slideDirection={direction}
            direction={1}
            displayInside
            disabled={activeIndex >= sectionCount - 1}
            ariaHidden
          />
        )}
      {direction === "horizontal" && sectionCount > 1 && (
        <div className="section-navigation-horizontal-markers" aria-hidden>
          {sections.map((_, i) => (
            <div
              key={i}
              className={classNames(
                "marker",
                i === activeIndex ? "active" : false
              )}
              onClick={() => {
                setActiveIndex(i);
                container.scrollTo({
                  [scrollAttr]: getSlide(i)[offsetAttr] - SCROLL_PADDING,
                  behavior: "smooth"
                });
              }}
            ></div>
          ))}
        </div>
      )}
      {showMenu && direction === "vertical" && (
        <>
          <div
            className={classNames("section-navigation", className)}
            aria-hidden
          >
            <div
              className={classNames("section-navigation-overlay", {
                visible: hovered || (introTimer && !isLargeScreen)
              })}
            />

            <div
              className="section-navigation-touch-area"
              id="section-navigation-touch-area"
              {...bindGestures()}
              onMouseEnter={() => {
                setHovered(true);
              }}
              onMouseLeave={() => {
                setHovered(false);
              }}
            >
              <AnimatePresence>
                <div
                  className={classNames("section-navigation-content", {
                    full: isLargeScreen,
                    visible:
                      isLargeScreen && activeIndex === 0 && scrollPos < 100,
                    intro: introTimer && !isLargeScreen,
                    touching: isTouchDevice && hovered
                  })}
                  ref={sectionNavigationContentRef}
                  id="section-navigation-content"
                >
                  {sections.map((s, i) => (
                    <SectionMarker
                      title={
                        s[0].settings.nav_title ||
                        t("section-navigation.top-of-page")
                      }
                      key={i}
                      index={i}
                      isActive={i === activeIndex}
                      isHovered={hovered && i === hoveredIndex}
                      setHovered={setHovered}
                      setHoveredIndex={setHoveredIndex}
                      onClick={() => {
                        container.scrollTo({
                          [scrollAttr]:
                            getSlide(i)[offsetAttr] - SCROLL_PADDING,
                          behavior: "smooth"
                        });
                      }}
                    />
                  ))}
                </div>
              </AnimatePresence>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SectionNavigation;
